import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Box,
  Typography,
  Paper,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  createCustomer,
  createCustomerTenancy,
  deleteCustomerTenancy,
  updateCustomer,
} from "../TenancyApi";
import { useEffect, useState } from "react";
import GuestForm from "./GuestForm";
import { updateRows } from "../CommonFunctions";
import AddReview from "../AddReview";

export default function GuestDetails({ tenancyId, customersDetails }) {
  // LOCAL UI STATE
  const [guestList, setGuestList] = useState([]);
  const [guestForm, setGuestForm] = useState({});
  const [showAddReview, setShowAddReview] = useState(false);

  // Normalize incoming data so each guest always has an "id"
  useEffect(() => {
    if (customersDetails && customersDetails.length > 0) {
      const normalized = customersDetails.map((g) => ({
        ...g,
        id: g.customerId, // ensure consistent id field
      }));
      setGuestList(normalized);
    } else {
      setGuestList([]);
    }
  }, [customersDetails]);

  // ADD GUEST
  function handleAddGuest() {
    setGuestForm({
      open: true,
      mode: "add",
      id: null,
      data: {
        fname: "",
        lname: "",
        email: "",
        phone: "",
        address: "",
        state: "",
      },
    });
  }

  // EDIT GUEST
  function handleEditGuest(guest) {
    setGuestForm({
      open: true,
      mode: "edit",
      id: guest.id, // normalized id
      data: { ...guest },
    });
  }

  function handleClose() {
    setGuestForm((prev) => ({ ...prev, open: false }));
  }

  // SAVE HANDLER COMING FROM FORM
  function handleSave(form) {
    if (guestForm.mode === "add") {
      createApiCustomer(form);
    } else {
      updateApiCustomer(form);
    }
  }

  // CREATE CUSTOMER
  const createApiCustomer = async (form) => {
    try {
      const response = await createCustomer(form);

      if (response) {
        const newGuest = {
          ...form,
          id: response.data, // backend created id
        };
        const res = await createCustomerTenancy(tenancyId, response.data);

        setGuestList((prev) => updateRows(prev, "create", null, newGuest));
      }
    } catch (error) {
      console.log(error);
    }
  };

  // UPDATE CUSTOMER
  const updateApiCustomer = async (form) => {
    try {
      const response = await updateCustomer(guestForm.id, form);

      if (response) {
        setGuestList((prev) =>
          updateRows(prev, "edit", guestForm.id, {
            ...form,
            id: guestForm.id, // ensure id stays attached
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  // DELETE CUSTOMER
  const handleDelete = async (guestId) => {
    try {
      await deleteCustomerTenancy(tenancyId, guestId);

      setGuestList((prev) => updateRows(prev, "delete", guestId));
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <Box sx={{ borderRadius: 1, overflow: "hidden", bgcolor: "white", px: 2 }}>
        <Box component={Paper} elevation={3}>
          {/* Header */}
          <Box
            sx={{
              bgcolor: "#e8f3ff",
              px: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: "18px",
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 0.2 }}>
              Guest Details
            </Typography>

            <Box>
              <Link
                to="#"
                style={{ color: "#0066ff", fontSize: "0.85rem", fontWeight: 500, marginRight: 50 }}
                onClick={() => setShowAddReview(true)}
              >
                Add Review
              </Link>

              <Link
                to="#"
                style={{ color: "#0066ff", fontSize: "0.85rem", fontWeight: 500 }}
                onClick={handleAddGuest}
              >
                Add Customer
              </Link>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table
              sx={{
                "& th": {
                  fontWeight: 600,
                  bgcolor: "white",
                  borderBottom: "1px solid #e3e3e3",
                  paddingY: 1,
                  paddingX: 2,
                },
                "& td": {
                  borderBottom: "1px solid #efefef",
                  fontSize: "0.92rem",
                  paddingY: 1,
                  paddingX: 2,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Reviews</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {/* If no guests */}
                {guestList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} style={{ textAlign: "center", padding: "16px" }}>
                      No guests found for this tenancy.
                    </TableCell>
                  </TableRow>
                )}

                {/* Render guests */}
                {guestList.map((guest) => (
                  <TableRow key={guest.id}>
                    <TableCell>
                      {guest.fname || guest.lname
                        ? `${guest.fname ?? ""} ${guest.lname ?? ""}`.trim()
                        : "—"}
                    </TableCell>

                    <TableCell>{guest.email || "—"}</TableCell>
                    <TableCell>{guest.phone || "—"}</TableCell>

                    <TableCell>
                      {guest.address
                        ? `${guest.address ?? ""} ${guest.address.state ?? ""}`
                        : "—"}
                    </TableCell>

                    <TableCell>{guest.reviewCount}</TableCell>

                    <TableCell>
                      <Link
                        to="#"
                        style={{ color: "#0066ff" }}
                        onClick={() => handleEditGuest(guest)}
                      >
                        Edit
                      </Link>

                      <span style={{ margin: "0 8px", color: "#aaa" }}>|</span>

                      <Link
                        to="#"
                        style={{ color: "#0066ff" }}
                        onClick={() => handleDelete(guest.id)}
                      >
                        Delete
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* Guest Form Modal */}
      <GuestForm
        open={guestForm.open}
        mode={guestForm.mode}
        data={guestForm.data}
        onClose={handleClose}
        onSave={handleSave}
      />

      {/* Add Review Modal */}
      {showAddReview && (
        <AddReview
          showAddReview={showAddReview}
          setShowAddReview={setShowAddReview}
          tenancyId={tenancyId}
          customersDetails={guestList}
        />
      )}
    </>
  );
}
